import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAdminOrdersChart } from "../../../api/api-hook";

type ChartPoint = { date: string; label: string; count: number };

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

const AdminDashboard = () => {
  const [chartData, setChartData]   = useState<ChartPoint[]>([]);
  const [refundCount, setRefundCount] = useState<number>(0);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getAdminOrdersChart()
      .then((r) => {
        if (r.success) { setChartData(r.chartData); setRefundCount(r.refundCount ?? 0); }
        else setError("Failed to load chart data.");
      })
      .catch(() => setError("Network error loading chart."))
      .finally(() => setLoading(false));
  }, []);

  const totalOrders = chartData.reduce((sum, d) => sum + d.count, 0);
  const peakDay     = chartData.reduce((best, d) => (d.count > best.count ? d : best), { date: "", label: "", count: 0 });

  return (
    <div>
      <h4 className="mb-1" style={{ color: BRAND_GREEN }}>Dashboard</h4>
      <p className="text-muted small mb-4">Welcome to the VSGA Admin Portal.</p>

      {/* Summary cards */}
      <div className="row g-3 mb-4">
        <div className="col-sm-4">
          <div className="border rounded p-3" style={{ borderLeft: `4px solid ${BRAND_GREEN}` }}>
            <div className="text-muted small text-uppercase fw-semibold mb-1">Packages (30 days)</div>
            <div className="fw-bold" style={{ fontSize: "1.6rem", color: BRAND_GREEN }}>
              {loading ? "—" : totalOrders}
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="border rounded p-3" style={{ borderLeft: `4px solid ${BRAND_GOLD}` }}>
            <div className="text-muted small text-uppercase fw-semibold mb-1">Peak Day</div>
            <div className="fw-bold" style={{ fontSize: "1.6rem", color: BRAND_GREEN }}>
              {loading ? "—" : peakDay.count > 0 ? `${peakDay.count} on ${peakDay.label}` : "—"}
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="border rounded p-3" style={{ borderLeft: `4px solid #dc3545` }}>
            <div className="text-muted small text-uppercase fw-semibold mb-1">Refunds (30 days)</div>
            <div className="fw-bold" style={{ fontSize: "1.6rem", color: BRAND_GREEN }}>
              {loading ? "—" : refundCount}
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="border rounded p-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-semibold mb-0" style={{ color: BRAND_GREEN }}>
            New Orders — Last 30 Days
          </h6>
          {!loading && !error && (
            <span className="text-muted small">
              {chartData.length > 0
                ? `${chartData[0].label} – ${chartData[chartData.length - 1].label}`
                : ""}
            </span>
          )}
        </div>

        {loading && (
          <div className="d-flex align-items-center justify-content-center" style={{ height: 240 }}>
            <div className="spinner-border spinner-border-sm me-2" style={{ color: BRAND_GREEN }} role="status" />
            <span className="text-muted small">Loading chart…</span>
          </div>
        )}

        {error && !loading && (
          <div className="alert alert-danger small py-2">{error}</div>
        )}

        {!loading && !error && (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#6c757d" }}
                interval={4}
                tickLine={false}
                axisLine={{ stroke: "#dee2e6" }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#6c757d" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{ fontSize: "0.8rem", borderRadius: 6, border: "1px solid #dee2e6" }}
                formatter={(value) => [value, "Orders"]}
                labelStyle={{ fontWeight: 600, color: BRAND_GREEN }}
              />
              <Bar dataKey="count" fill={BRAND_GREEN} radius={[3, 3, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
